import { t, formatNumber } from '../../../shared/i18n/index.js';
import { sizeLabel } from '../../../engine/services/storage-service.js';

/**
 * Independent progress cards: concurrent transfers cannot dismiss each other.
 * With `onCancel`, the card shows a Cancel button while the caller keeps the
 * operation cancellable (`setCancellable(false)` once cancelling would no
 * longer be safe); `cancelled()` ends the card as "nothing was changed".
 */
/** The corner where the cards are shown (created when first needed). */
export function progressRegion() {
  let region = document.getElementById('operation-progress');
  if (!region) {
    region = document.createElement('section');
    region.id = 'operation-progress';
    region.setAttribute('aria-label', t('vault.progress.region'));
    document.body.appendChild(region);
  }
  return region;
}

export function beginOperation(title, { onCancel } = {}) {
  if (typeof document === 'undefined' || !document.body?.appendChild) {
    return { update() {}, finish() {}, fail() {}, cancelled() {}, dismiss() {}, setCancellable() {}, async paint() {} };
  }
  const region = progressRegion();
  region.querySelectorAll('[data-state="complete"]').forEach(card => card.remove());
  const card = document.createElement('div');
  card.className = 'operation-progress-card';
  const heading = document.createElement('strong');
  heading.textContent = title;
  const label = document.createElement('span');
  label.className = 'operation-progress-label';
  label.setAttribute('role', 'status');
  const bar = document.createElement('progress');
  bar.max = 100;
  bar.setAttribute('aria-label', title);
  const detail = document.createElement('span');
  detail.className = 'operation-progress-detail';
  const close = document.createElement('button');
  close.type = 'button';
  close.textContent = t('vault.progress.dismiss');
  close.hidden = true;
  const remove = () => { card.remove(); if (!region.children.length) region.remove(); };
  close.addEventListener('click', remove);
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'operation-progress-cancel';
  cancel.textContent = t('common.cancel');
  cancel.hidden = !onCancel;
  let cancelling = false;
  cancel.addEventListener('click', () => {
    if (ended || cancelling) return;
    cancelling = true;
    cancel.disabled = true;
    label.textContent = t('vault.progress.cancelling');
    onCancel();
  });
  card.append(heading, label, bar, detail, cancel, close);
  region.prepend(card);
  region.scrollTop = 0;
  let ended = false;
  const update = ({ label: text = t('vault.progress.processing'), current, total, detail: extra = '', unit = '' } = {}) => {
    if (ended) return;
    if (!cancelling && label.textContent !== text) label.textContent = text;
    if (Number.isFinite(total) && total > 0 && Number.isFinite(current)) {
      const count = Math.min(total, Math.max(0, current));
      bar.value = count / total * 100;
      detail.textContent = extra || t('vault.progress.ratio', { percent: plain(Math.floor(bar.value)),
        ...(unit === 'bytes' ? { current: formatBytes(count), total: formatBytes(total) } : { current: plain(Math.floor(count)), total: plain(total) }) });
    } else {
      bar.removeAttribute('value');
      detail.textContent = extra || (unit === 'bytes' && current ? t('vault.progress.received', { size: formatBytes(current) }) : t('vault.progress.keepOpen'));
    }
  };
  const finish = (text = t('vault.progress.complete'), warning = false) => {
    if (ended) return;
    update({ label: text, current: 1, total: 1, detail: t(warning ? 'vault.progress.reviewWarnings' : 'vault.progress.complete') });
    ended = true;
    cancel.hidden = true;
    card.dataset.state = warning ? 'warning' : 'complete';
    close.hidden = false;
    if (!warning) setTimeout(remove, 1500);
  };
  const fail = error => {
    if (ended) return;
    ended = true;
    cancel.hidden = true;
    label.textContent = t('vault.progress.failed');
    detail.textContent = error?.message || String(error || t('vault.progress.retry'));
    bar.hidden = true;
    card.dataset.state = 'error';
    close.hidden = false;
  };
  const cancelled = (text = t('vault.progress.nothingChanged')) => {
    if (ended) return;
    ended = true;
    cancel.hidden = true;
    label.textContent = t('vault.progress.cancelled');
    detail.textContent = text;
    bar.hidden = true;
    card.dataset.state = 'cancelled';
    close.hidden = false;
    setTimeout(remove, 4000);
  };
  const setCancellable = (value) => {
    if (!onCancel || ended || cancelling) return;
    cancel.hidden = !value;
  };
  // Ends the card without a verdict, when the page itself shows the outcome.
  const dismiss = () => {
    if (ended) return;
    ended = true;
    remove();
  };
  update();
  return { update, finish, fail, cancelled, dismiss, setCancellable,
    // Let the browser paint before CPU-heavy serialization/rendering.
    paint: () => new Promise(resolve => setTimeout(resolve, 0)) };
}

function formatBytes(bytes) {
  if (bytes < 1024) return sizeLabel(Math.floor(bytes), 'b', 0);
  if (bytes < 1024 * 1024) return sizeLabel(bytes / 1024, 'kb', 1);
  return sizeLabel(bytes / (1024 * 1024), 'mb', 1);
}

/** A count without digit grouping ("1234"), as before translation. */
function plain(value) {
  return formatNumber(value, { useGrouping: false, maximumFractionDigits: 20 });
}

export async function withOperation(title, work, describeResult = () => [t('vault.progress.complete')]) {
  const progress = beginOperation(title);
  try {
    await progress.paint();
    const result = await work(progress);
    progress.finish(...describeResult(result));
    return result;
  } catch (error) { progress.fail(error); throw error; }
}
